import { Controller } from 'react-hook-form';
import { validate } from 'uuid';
import {
  IconLoader,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../hoc/new-application/types';
import { BusniessActivitiesTypes } from './types';
import StepPartBlock from '../../common/step-part-block';
import InfoRow from '../../common/info-row';

import commonStyles from '../../common/styles.module.css';

const BusinessActivities = ({
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  busniessActivities,
  customerMainCleints,
  salesChannels,
  questionnarieSetValue,
  questionnarieFields,
  customerSourceOfFunds,
  companyLegalName,
  questionnarieIsLoading,
}: BusniessActivitiesTypes) => (
  <StepPartBlock title="Customer Business Activities" zIndex={14}>
    <>
      {questionnarieIsLoading && (
        <div className={commonStyles.dictionaryLoader}>
          <IconLoader />
        </div>
      )}
      {!questionnarieIsLoading && (
        <>
          <InfoRow
            title="Does the Customer apply KYC procedures?"
            id="customerAppliesKycProcedures"
            style={{
              position: 'relative',
              zIndex: 14,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="customerAppliesKycProcedures"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['customerAppliesKycProcedures']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.customerAppliesKycProcedures?.message
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title="Which of the following activities does the Customer carry out? (select all that apply)"
            id="businessActivitiesIds"
            style={{
              position: 'relative',
              zIndex: 13,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="businessActivitiesIds"
              defaultValue={[]}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={busniessActivities?.map((item) => ({
                    name: item.value,
                    value: item.id,
                  }))}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    questionnarieClearErrors([
                      'businessActivitiesIds',
                      'hasBusinessActivity',
                    ]);

                    let nextValues: string[] = e.target.value as string[];

                    if (
                      (e.target.value as string[])[
                        (e.target.value as string[]).length - 1
                      ] === 'NONE'
                    ) {
                      nextValues = ['NONE'];
                      questionnarieSetValue('hasBusinessActivity', false);
                    } else if (
                      (e.target.value as string[])?.find((item) =>
                        validate(item),
                      )
                    ) {
                      if (
                        (e.target.value as string[])?.find(
                          (item) => item === 'NONE',
                        )
                      ) {
                        nextValues = (e.target.value as string[])?.filter(
                          (item) => item !== 'NONE',
                        );
                      }

                      if (!questionnarieFields?.hasBusinessActivity) {
                        questionnarieSetValue('hasBusinessActivity', true);
                      }
                    }

                    onChange(nextValues);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.businessActivitiesIds?.message}
                  mode={SelectType.MULTIPLE}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title="Customer's main clients"
            id="customersMainClientsIds"
            style={{
              position: 'relative',
              zIndex: 12,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="customersMainClientsIds"
              defaultValue={[]}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={
                    customerMainCleints?.map((item) => ({
                      name: item.value,
                      value: item.id,
                    })) || []
                  }
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['customersMainClientsIds']);

                    if (
                      !(e.target.value as string[])?.includes('Other') &&
                      questionnarieFields?.customersMainClientsOther
                    ) {
                      questionnarieClearErrors(['customersMainClientsOther']);
                      questionnarieSetValue('customersMainClientsOther', '');
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.customersMainClientsIds?.message}
                  mode={SelectType.MULTIPLE}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.customersMainClientsIds?.find((item) =>
            customerMainCleints
              ?.find((client) => item === client.id)
              ?.value?.includes('Other'),
          ) && (
            <InfoRow
              title="Provide details"
              id="customersMainClientsOther"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="customersMainClientsOther"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['customersMainClientsOther']);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    error={
                      questionnarieErrors?.customersMainClientsOther?.message
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
          <InfoRow
            title="How does the Customer sell products and/or services?"
            id="salesChannelsIds"
            style={{
              position: 'relative',
              zIndex: 11,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="salesChannelsIds"
              defaultValue={[]}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={
                    salesChannels?.map((item) => ({
                      name: item.value,
                      value: item.id,
                    })) || []
                  }
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['salesChannelsIds']);

                    if (
                      !(e.target.value as string[])?.includes('Other') &&
                      questionnarieFields?.salesChannelsOther
                    ) {
                      questionnarieClearErrors(['salesChannelsOther']);
                      questionnarieSetValue('salesChannelsOther', '');
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.salesChannelsIds?.message}
                  mode={SelectType.MULTIPLE}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.salesChannelsIds?.find((item) =>
            salesChannels
              ?.find((saleChannel) => item === saleChannel.id)
              ?.value?.includes('Other'),
          ) && (
            <InfoRow
              title="Provide details"
              id="salesChannelsOther"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="salesChannelsOther"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['salesChannelsOther']);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    error={questionnarieErrors?.salesChannelsOther?.message}
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
          <InfoRow
            title="Number of employees"
            id="numberOfEmployees"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="numberOfEmployees"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter number..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['numberOfEmployees']);
                  }}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.numberOfEmployees?.message}
                />
              )}
            />
          </InfoRow>
          <InfoRow
            title={`Source of funds to be credited to the account in ${companyLegalName}`}
            id="sourcesOfFundsIds"
            style={{
              position: 'relative',
              zIndex: 10,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="sourcesOfFundsIds"
              defaultValue={[]}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={
                    customerSourceOfFunds?.map((item) => ({
                      name: item.value,
                      value: item.id,
                    })) || []
                  }
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['sourcesOfFundsIds']);

                    if (
                      !(e.target.value as string[])?.includes('Other') &&
                      questionnarieFields?.sourcesOfFundsOther
                    ) {
                      questionnarieClearErrors(['sourcesOfFundsOther']);
                      questionnarieSetValue('sourcesOfFundsOther', '');
                    }

                    if (
                      !(e.target.value as string[])?.includes('Other') &&
                      questionnarieFields?.sourcesOfFundsLoanCreditInstitution
                    ) {
                      questionnarieClearErrors([
                        'sourcesOfFundsLoanCreditInstitution',
                      ]);
                      questionnarieSetValue(
                        'sourcesOfFundsLoanCreditInstitution',
                        '',
                      );
                    }

                    if (
                      !(e.target.value as string[])?.includes('Other') &&
                      questionnarieFields?.sourcesOfFundsLoanBusinessPartner
                    ) {
                      questionnarieClearErrors([
                        'sourcesOfFundsLoanBusinessPartner',
                      ]);
                      questionnarieSetValue(
                        'sourcesOfFundsLoanBusinessPartner',
                        '',
                      );
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.sourcesOfFundsIds?.message}
                  mode={SelectType.MULTIPLE}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
            customerSourceOfFunds
              ?.find((client) => item === client.id)
              ?.value?.includes('Other'),
          ) && (
            <InfoRow
              title="Provide details"
              id="sourcesOfFundsOther"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="sourcesOfFundsOther"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['sourcesOfFundsOther']);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    error={questionnarieErrors?.sourcesOfFundsOther?.message}
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
          {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
            customerSourceOfFunds
              ?.find((client) => item === client.id)
              ?.value?.includes('Loan from a credit institution'),
          ) && (
            <InfoRow
              title="Credit institution which have provided loan"
              id="sourcesOfFundsLoanCreditInstitution"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="sourcesOfFundsLoanCreditInstitution"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors([
                        'sourcesOfFundsLoanCreditInstitution',
                      ]);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    error={
                      questionnarieErrors?.sourcesOfFundsLoanCreditInstitution
                        ?.message
                    }
                  />
                )}
              />
            </InfoRow>
          )}
          {questionnarieFields?.sourcesOfFundsIds?.find((item) =>
            customerSourceOfFunds
              ?.find((client) => item === client.id)
              ?.value?.includes('Loan from a business partner'),
          ) && (
            <InfoRow
              title="Business partner which provided loan"
              id="sourcesOfFundsLoanBusinessPartner"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="sourcesOfFundsLoanBusinessPartner"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors([
                        'sourcesOfFundsLoanBusinessPartner',
                      ]);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    error={
                      questionnarieErrors?.sourcesOfFundsLoanCreditInstitution
                        ?.message
                    }
                  />
                )}
              />
            </InfoRow>
          )}

          <InfoRow
            title="Does the Customer have accounts in other financial institutions?"
            id="hasAccountsInOtherFinancialInstitutions"
            style={{
              position: 'relative',
              zIndex: 9,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="hasAccountsInOtherFinancialInstitutions"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'hasAccountsInOtherFinancialInstitutions',
                    ]);

                    if (
                      e.target.value === 'no' &&
                      questionnarieFields?.otherFinancialInstitutions
                    ) {
                      questionnarieClearErrors(['otherFinancialInstitutions']);
                      questionnarieSetValue('otherFinancialInstitutions', '');
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={
                    questionnarieErrors?.hasAccountsInOtherFinancialInstitutions
                      ?.message
                  }
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
          {questionnarieFields?.hasAccountsInOtherFinancialInstitutions ===
            'yes' && (
            <InfoRow
              title="Financial institutions at which customer has accounts"
              id="otherFinancialInstitutions"
              className={commonStyles.noFlex}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="otherFinancialInstitutions"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Textarea
                    name={name}
                    title=""
                    placeholder="Enter details..."
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['otherFinancialInstitutions']);
                    }}
                    className={`borderless ${commonStyles.pepTextarea} ${commonStyles.stepOneTextarea}`}
                    error={
                      questionnarieErrors?.otherFinancialInstitutions?.message
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
        </>
      )}
    </>
  </StepPartBlock>
);

export default BusinessActivities;
